import React from 'react';

import Icon from '../claims/shared/Icon';

function promptForLink(editor) {
  const previous = editor.getAttributes('link').href || '';
  const url = window.prompt('URL (http(s) or mailto):', previous);
  if (url === null) return;
  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  if (!/^(https?:|mailto:)/i.test(url)) {
    window.alert('Only http, https, or mailto URLs are allowed.');
    return;
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
}

// Frozen toolbar (2026-05-28 design + 06-UI-SPEC): order, icons, and labels
// may not change. Every button carries aria-label + title = action name.
const BUTTONS = [
  {
    key: 'bold',
    'aria-label': 'Bold',
    icon: 'bold',
    toggle: true,
    isActive: (e) => e.isActive('bold'),
    run: (e) => e.chain().focus().toggleBold().run(),
  },
  {
    key: 'italic',
    'aria-label': 'Italic',
    icon: 'italic',
    toggle: true,
    isActive: (e) => e.isActive('italic'),
    run: (e) => e.chain().focus().toggleItalic().run(),
  },
  {
    key: 'h2',
    'aria-label': 'Heading 2',
    icon: 'heading-2',
    toggle: true,
    isActive: (e) => e.isActive('heading', { level: 2 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    key: 'h3',
    'aria-label': 'Heading 3',
    icon: 'heading-3',
    toggle: true,
    isActive: (e) => e.isActive('heading', { level: 3 }),
    run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    key: 'ul',
    'aria-label': 'Bulleted list',
    icon: 'list',
    toggle: true,
    isActive: (e) => e.isActive('bulletList'),
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    key: 'ol',
    'aria-label': 'Numbered list',
    icon: 'list-ordered',
    toggle: true,
    isActive: (e) => e.isActive('orderedList'),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    key: 'link',
    'aria-label': 'Link',
    icon: 'link',
    toggle: true,
    isActive: (e) => e.isActive('link'),
    run: promptForLink,
  },
  {
    key: 'undo',
    'aria-label': 'Undo',
    icon: 'undo-2',
    toggle: false,
    isActive: () => false,
    run: (e) => e.chain().focus().undo().run(),
  },
  {
    key: 'redo',
    'aria-label': 'Redo',
    icon: 'redo-2',
    toggle: false,
    isActive: () => false,
    run: (e) => e.chain().focus().redo().run(),
  },
];

export default function Toolbar({ editor }) {
  if (!editor) return null;
  return (
    <div className="rte-toolbar" role="toolbar" aria-label="Formatting">
      {BUTTONS.map(({ key, 'aria-label': label, icon, toggle, isActive, run }) => {
        const active = isActive(editor);
        return (
          <button
            key={key}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={toggle ? active : undefined}
            onClick={() => run(editor)}
            className={`rte-toolbar-btn${active ? ' rte-toolbar-btn--active' : ''}`}
          >
            <Icon name={icon} size={16} />
          </button>
        );
      })}
    </div>
  );
}
